import type {
  FallbackCenter,
  MapLayer,
  MapFeaturesResponse,
  MapViewport,
  NearCenter
} from "../redux/global-map.types"

// `MapFeature` and `MapCluster` both carry `latitude`/`longitude` plus other
// fields the fit math never looks at, so bounds are computed against this
// narrower shape rather than a `MapFeature[] | MapCluster[]` union — a plain
// `MapFeature[]` or `MapCluster[]` is directly assignable to it.
interface FitBoundsTarget {
  latitude: number
  longitude: number
}

interface FitBounds {
  bounds: FitBoundsTarget[]
}

// Same constant, same reasoning, as the backend's own `_near_bbox_q`
// (`ezbuild_back/apps/global_map/query.py`) — 1 degree of latitude is ~69
// miles everywhere on Earth; longitude degrees shrink toward the poles
// (`cos(latitude)`), and `max(..., 0.01)` guards a near-polar origin
// against dividing by (near) zero. Kept in sync by hand, the same
// convention every other cross-repo constant on this feature already
// follows — there is no shared package between the two repos to import it
// from.
const MILES_PER_DEGREE_LATITUDE = 69.0

// The near-radius filter's own bounding box — two opposite corners are
// enough for `MapCanvas.tsx`'s `applyFit` (it only reads `Math.max`/`min`
// across whatever points `bounds` holds). Deliberately the FULL circle's
// box, not the tighter box around whatever markers happen to be inside it:
// "Look Around a Zip Code" makes the circle itself the area of interest, so
// the camera frames it whole "at any radius" — zero results, one, or fifty
// — per explicit product direction. Every marker the current filters
// return is by definition within the radius, so this box always contains
// them too; a separate "fit to features" pass is never needed on top of it.
function nearRadiusFitBounds(center: NearCenter, radiusMi: number): FitBounds {
  const latDelta = radiusMi / MILES_PER_DEGREE_LATITUDE
  const lngDelta =
    radiusMi /
    (MILES_PER_DEGREE_LATITUDE *
      Math.max(Math.cos((center.latitude * Math.PI) / 180), 0.01))

  return {
    bounds: [
      {
        latitude: center.latitude + latDelta,
        longitude: center.longitude + lngDelta
      },
      {
        latitude: center.latitude - latDelta,
        longitude: center.longitude - lngDelta
      }
    ]
  }
}

// Spec §5: what the map should frame once a response arrives. `null` means
// there is nothing to fit yet (initial load, before the first response).
// `nearRadiusMi` is the committed "Look Around a Zip Code" radius (or `null`
// when that filter isn't active) — passed in separately rather than read
// off `data` because the server's response has no reason to carry a purely
// client-side camera choice.
function computeFit(
  data: MapFeaturesResponse | null,
  nearRadiusMi: number | null = null
): FallbackCenter | FitBounds | null {
  if (data === null) return null

  // Real, reported direction: "At any radius the map should zoom in and out
  // so it will always show the center and the entire circle" — this takes
  // priority over every branch below, including the zero-results one:
  // `data.near_center` is echoed back whenever the filter is active
  // regardless of how many records it matched (see `ezbuild_back`'s
  // `MapFeaturesView.get`), so a zero-result radius still centers and
  // frames itself correctly, without a separate fallback-zoom guess.
  if (data.near_center && nearRadiusMi != null) {
    return nearRadiusFitBounds(data.near_center, nearRadiusMi)
  }

  if (data.total === 0) return data.fallback_center

  if (data.mode === "features" && data.features.length) {
    return { bounds: data.features }
  }

  return { bounds: data.clusters }
}

// Spec: "auto-fit only before manual exploration". `prev` is accepted (rather
// than deriving this from a boolean alone) so a future increment that needs
// to compare against the previous viewport — e.g. only auto-fitting when the
// viewport actually changed — can do so without a signature change; this
// increment has no filter commits, so the only thing that matters yet is
// whether the user has panned since the page loaded.
function shouldAutoFit(
  _prev: MapViewport | null,
  userPanned: boolean
): boolean {
  return !userPanned
}

// A client-side filter over an already-fetched response — 2026-09-19: the
// layer toggle now ALSO drives the fetch itself (`entities`, in
// `useMapFeatures.ts`), so in steady state `data` already only contains the
// active layers and this is a no-op. It stays as a defensive second pass for
// the transient window between toggling a layer and the resulting fetch
// resolving — without it, a stale response fetched for the PREVIOUS layer
// selection would flash the just-disabled layer's markers for one frame. A
// cluster carries no per-feature entity type, so it is kept whenever it
// holds at least one record of an active layer. Shared between `MapTab.tsx`
// (the full page, which needs every field of `MapFeaturesResponse` preserved
// on the way through — `project_total`/`lead_total` etc.) and `MapWidget.tsx`
// (the home dashboard tile, which only ever carries the narrower
// `mode`/`features`/`clusters` shape `MapCanvas.tsx` itself reads) — generic
// over `T` so each caller gets back exactly the shape it passed in.
function filterByActiveLayers<
  T extends Pick<MapFeaturesResponse, "mode" | "features" | "clusters">
>(data: T, activeLayers: MapLayer[]): T {
  if (data.mode === "features") {
    return {
      ...data,
      features: data.features.filter((feature) =>
        activeLayers.includes(feature.entity_type)
      )
    }
  }

  const activeSet = new Set(activeLayers)
  return {
    ...data,
    clusters: data.clusters.filter(
      (cluster) =>
        (activeSet.has("project") && cluster.project_count > 0) ||
        (activeSet.has("lead") && cluster.lead_count > 0)
    )
  }
}

export { computeFit, filterByActiveLayers, shouldAutoFit }
export type { FitBounds }
